import React from 'react'
import Markdown from 'react-markdown'
import { withRouter } from 'react-router'

import camelCase from 'camelcase'
import { compose, withHandlers, withProps } from 'recompose'

import { OPTIMIZELY_FEATURE_FLAGS } from 'app-constants'
import { withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { withOptimizely } from 'utils/Optimizely'
import { getVariation } from 'utils/Optimizely/variationsUtils'

import { getHeroText } from '../withHeroText/withHeroText'
import withRemoteStrings from '../withRemoteStrings/withRemoteStrings'
import { renderers } from './renderers'

type Props = {
  liveContent: (key: string, options?: LiveContentOptions) => any
}

export type LiveContentProps = Props

export type LiveContentOptions = {
  fallback?: any
  renderer?: 'markdown' | 'raw'
  remoteVariableKey?: string
}

const liveContentExperimentsMap = new Map([
  [FlowRoute.Signup, { experimentKey: 'growth-1661_create_account' }],
  [FlowRoute.Funding, { experimentKey: 'growth-1661_bank_interstitial' }],
  [FlowRoute.PersonalInfo, { experimentKey: 'growth-1661_personal_info' }],
])

const getLiveVariables = key => {
  const features = JSON.parse(window.sessionStorage.getItem(OPTIMIZELY_FEATURE_FLAGS))
  if (features) {
    return features[key]?.variables
  }
  return null
}

export const getFeatureVariable = (featureFlagName: string, variableKey: string): string | null => {
  const liveVariables = getLiveVariables(featureFlagName)
  if (liveVariables) {
    const liveVariable = liveVariables.find(variable => variable.key === variableKey)
    if (liveVariable && liveVariable.defaultValue) {
      return formatValue(liveVariable)
    }
  }

  return null
}

const formatValue = liveVariable => {
  let formatted
  switch (liveVariable.type) {
    case 'boolean':
      formatted = liveVariable.defaultValue === 'true'
      break
    case 'integer':
      formatted = parseInt(liveVariable.defaultValue, 10)
      break
    case 'double':
      formatted = parseFloat(liveVariable.defaultValue)
      break
    default:
      formatted = liveVariable.defaultValue
      break
  }
  return formatted
}

const fetchLiveContent = (key, pathname) => {
  // LIVE VARIABLE EXPERIMENT
  const experimentsMap = liveContentExperimentsMap.get(pathname)
  if (experimentsMap && experimentsMap.experimentKey) {
    const variation = getVariation(experimentsMap.experimentKey)
    if (variation) {
      const featureFlagName = `${experimentsMap.experimentKey}_${variation}`
      const liveVariables = getLiveVariables(featureFlagName)
      if (liveVariables && liveVariables.length > 0) {
        const liveVariable = liveVariables.find(variable => variable.key === key)
        if (liveVariable && liveVariable.defaultValue) {
          return formatValue(liveVariable)
        }
      }
    }
  }
  return null
}

const fetchHeroCopy = (flow, key, pathname) => {
  const heroText = getHeroText(flow, pathname)
  const heroPropName = camelCase(key.slice(5))
  // Disabling ESLint for this line for no reason other than the code existed like this before this ESLint rule went into effect.
  // If you're here from the future, please resolve this ESLint error!
  // eslint-disable-next-line no-prototype-builtins
  if (heroText && heroText.hasOwnProperty(heroPropName)) {
    return heroText[heroPropName]
  }
  return null
}

const renderValue = (value, options) => {
  // Hero Copy can contain react elements, so no rendering is needed
  if (value && typeof value === 'object') {
    return value
  } else if (value && options.renderer === 'markdown') {
    return <Markdown source={value} renderers={renderers} />
  } else {
    return value
  }
}

const getLiveContent = (
  key: string,
  opts: LiveContentOptions = {},
  flow: string,
  pathname: string,
  getRemoteString: any,
) => {
  const options = { ...{ renderer: 'markdown' }, ...opts }
  let value = null

  value = fetchLiveContent(key, pathname)

  // REMOTE STRING
  if (!value && options.remoteVariableKey) {
    value = getRemoteString(options.remoteVariableKey, null, true)
  }

  // HERO TEXT
  if (!value && key.startsWith('hero_')) {
    value = fetchHeroCopy(flow, key, pathname)
  }

  // FALLBACK
  if (!value) {
    value = options.fallback
  }

  return renderValue(value, options)
}

export default compose<Props, {}>(
  withRouter,
  withFlowRouter,
  withOptimizely,
  withRemoteStrings,
  withHandlers({
    liveContent: ({ flow, history, getRemoteString }) => (
      key: string,
      options?: LiveContentOptions,
    ) => {
      return getLiveContent(key, options, flow, history.location.pathname, getRemoteString)
    },
    getFeatureVariable: () => (featureFlagName: string, variableKey: string) => {
      return getFeatureVariable(featureFlagName, variableKey)
    },
  }),
  withProps(({ liveContent }) => {
    return {
      liveContent,
    }
  }),
)
